import { useMemo } from "react";
import { QueryKey, UseQueryOptions, useQuery, QueryFunctionContext } from "react-query";

import { useGetOrganisationFeatureFlag } from "@tether-web-portal/api/organisations/hooks";
import { useGetUserSetting } from "@tether-web-portal/api/settings/hooks";
import { ORGANISATION_FEATURE_FLAGS } from "@tether-web-portal/constants/featureFlags";
import { UserSettingName } from "@tether-web-portal/types/UserSetting";
import { useAggregatedQueries, AggregatedQueriesResponse } from "./useAggregatedQueries";

type AggregatesV2QueryFunction<T> = (context: QueryFunctionContext<QueryKey>, useV2Aggregates: boolean) => T | Promise<T>;

export function useAggregatesV2Query<TQueryFnData = unknown, TError = unknown>(queryKey: QueryKey, queryFn: AggregatesV2QueryFunction<TQueryFnData>, options?: UseQueryOptions<TQueryFnData, TError>) {

  const { value: orgUseV2Aggregates, isLoading: orgFlagsLoading } = useGetOrganisationFeatureFlag(ORGANISATION_FEATURE_FLAGS.USE_V2_AGGREGATES);
  const { value: userUseV2Aggregates, isLoading: userSettingLoading } = useGetUserSetting<boolean>(UserSettingName.UseV2Aggregates, "boolean");

  const queryItem = useQuery(queryKey, (context) => queryFn(context, Boolean(orgUseV2Aggregates || userUseV2Aggregates)), {
    ...options,
    enabled: (options?.enabled ?? true) && !orgFlagsLoading && !userSettingLoading,
  });

  return useMemo(() => ({
    ...queryItem,
    isLoading: queryItem.isLoading || orgFlagsLoading || userSettingLoading,
  }), [queryItem, orgFlagsLoading, userSettingLoading]);
}

export function useAggregatesV2Queries<
  TQueryFnData = unknown,
  TError = unknown
>(
  queries: (Omit<UseQueryOptions<TQueryFnData, TError>, 'queryFn'> & { queryFn: AggregatesV2QueryFunction<TQueryFnData> })[]
): AggregatedQueriesResponse<TQueryFnData, TError> {
  const { value: orgUseV2Aggregates, isLoading: orgFlagsLoading } = useGetOrganisationFeatureFlag(ORGANISATION_FEATURE_FLAGS.USE_V2_AGGREGATES);
  const { value: userUseV2Aggregates, isLoading: userSettingLoading } = useGetUserSetting<boolean>(UserSettingName.UseV2Aggregates, "boolean");

  // Transform queries to wrap the queryFn with the useV2Aggregates parameter
  const transformedQueries = useMemo(() => {
    const useV2Aggregates = Boolean(orgUseV2Aggregates || userUseV2Aggregates);
    
    return queries.map(query => ({
      ...query,
      queryFn: (context: QueryFunctionContext<QueryKey>) => query.queryFn(context, useV2Aggregates),
      enabled: (query.enabled ?? true) && !orgFlagsLoading && !userSettingLoading,
    }));
  }, [queries, orgUseV2Aggregates, userUseV2Aggregates, orgFlagsLoading, userSettingLoading]);

  // Use the base useAggregatedQueries hook
  const aggregatedResult = useAggregatedQueries(transformedQueries);

  // Extend the result to include the loading states from feature flags and user settings
  return useMemo(() => ({
    ...aggregatedResult,
    isLoading: aggregatedResult.isLoading || orgFlagsLoading || userSettingLoading,
  }), [aggregatedResult, orgFlagsLoading, userSettingLoading]);
}